import { z } from 'zod';
import { BaseCoverageSchema, BaseEstimateSchema, BaseLineItemSchema } from './base.js';
import { money, qty } from './primitives.js';
import { VendorNameSchema } from './vendor.js';
export const EstimateContactSchema = z.object({
    role: z.string(),
    name: z.string(),
    phone: z.string().optional(),
    email: z.string().optional(),
    companyName: z.string().optional(),
});
export const EstimateAddressSchema = z.object({
    type: z.string().optional(),
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    primary: z.boolean().optional(),
});
export const EstimateGroupSchema = z.lazy(() => z.object({
    description: z.string(), // i.e. "Kitchen", "Dining Room", or whatever
    total: money.optional(),
    replacementCost: money.optional(),
    actualCashValue: money.optional(),
    tax: money.optional(),
    children: z.array(EstimateGroupSchema).optional(),
    itemLineNumbers: z.array(qty).optional(),
}));
export const VeriskVendorSchema = z.object({
    transactionId: z.string().optional(),
    majorVersion: qty.optional(),
    minorVersion: qty.optional(),
    priceList: z.string().optional(),
    laborEfficiency: z.string().optional(),
    carrierId: z.string().optional(),
    recipientsXnAddress: z.string().optional(),
    companyName: z.string().optional(),
    companyInfo: z.string().optional(),
    openingStatement: z.string().optional(),
    groups: EstimateGroupSchema.optional(),
});
export const SymbilityVendorSchema = z.object({
    estimateId: qty.optional(),
    assignmentId: qty.optional(),
    status: z
        .enum([
        'InProgress',
        'Completed',
        'Approved',
        'ReadyForReview',
        'RejectedReview',
        'RejectedApproval',
        'Unknown',
        'Cancelled',
    ])
        .optional(),
    authorCompanyName: z.string().optional(),
    type: z.string().optional(),
    totalMaterials: money.optional(),
    totalLabor: money.optional(),
    totalEquipment: money.optional(),
    totalOverhead: money.optional(),
    totalProfit: money.optional(),
    totalTax1: money.optional(),
    totalTax2: money.optional(),
    totalTax3: money.optional(),
    totalTax4: money.optional(),
});
export const EstimateCoverageSchema = BaseCoverageSchema.extend({
    coverageType: qty.optional(),
    amountPayableReplacementCost: money.optional(),
    amountPayableActualCashValue: money.optional(),
});
export const EstimateLineItemSchema = BaseLineItemSchema.extend({
    age: qty.optional(),
    confidence: z
        .object({
        score: qty.optional(),
        reasoning: z.string().optional(),
    })
        .optional(),
    laborHours: qty.optional(),
    lifeExpectancy: qty.optional(),
    trade: z.string().optional(),
    unitEquipment: money.optional(),
    unitLabor: money.optional(),
    unitMaterial: money.optional(),
});
export const EstimateSchema = BaseEstimateSchema.extend({
    addresses: z.array(EstimateAddressSchema).optional(),
    comments: z.string().optional(),
    companyName: z.string().optional(),
    contacts: z.array(EstimateContactSchema).optional(),
    coverages: z.array(EstimateCoverageSchema).optional(),
    groups: EstimateGroupSchema.optional(),
    items: z.array(EstimateLineItemSchema).optional(),
    source: VendorNameSchema.or(z.string()).optional(),
    vendor: z
        .object({
        verisk: VeriskVendorSchema.optional(),
        symbility: SymbilityVendorSchema.optional(),
    })
        .optional(),
});
// zod-to-openapi stack-overflows on EstimateGroupSchema's z.lazy recursion (incl. vendor.verisk.groups). Fixed-depth group tree for docs/runtime ApiSchema only; handlers still return full Estimate.
const EstimateGroupOpenApiSchema = z.object({
    description: z.string(),
    total: money.optional(),
    replacementCost: money.optional(),
    actualCashValue: money.optional(),
    tax: money.optional(),
    itemLineNumbers: z.array(qty).optional(),
    children: z.array(z.object({ description: z.string() }).loose()).optional(),
});
const VeriskVendorOpenApiSchema = VeriskVendorSchema.omit({ groups: true }).extend({
    groups: EstimateGroupOpenApiSchema.optional(),
});
export const EstimateOpenApiSchema = BaseEstimateSchema.extend({
    addresses: z.array(EstimateAddressSchema).optional(),
    comments: z.string().optional(),
    companyName: z.string().optional(),
    contacts: z.array(EstimateContactSchema).optional(),
    coverages: z.array(EstimateCoverageSchema).optional(),
    groups: EstimateGroupOpenApiSchema.optional(),
    items: z.array(EstimateLineItemSchema).optional(),
    source: VendorNameSchema.or(z.string()).optional(),
    vendor: z
        .object({
        verisk: VeriskVendorOpenApiSchema.optional(),
        symbility: SymbilityVendorSchema.optional(),
    })
        .optional(),
});
